import { PDFReportType } from 'modules/productAnalytics/types';
import { z } from 'zod';
import { ChartDisplay, SegmentTimeFrequency } from '../types';

// Define constants for schema attributes
export const PDF_REPORT_FORM_FIELD_NAMES = {
  TYPE: 'type',
  ENV: 'env',
  REPORT_DETAILS: {
    NAME: 'reportDetails.name',
    USERNAME: 'reportDetails.username',
  },
  ORGANIZATION_DETAILS: {
    ORG_NAME: 'organizationDetails.orgName',
    PROJ_NAME: 'organizationDetails.projName',
    ORG_ID: 'organizationDetails.orgId',
    PROJ_ID: 'organizationDetails.projId',
  },
  FORM_SETTINGS: {
    AUDIENCE_SEGMENT: 'formSettings.audienceSegment',
    LOCATION_REGION: 'formSettings.locationRegion',
    CHART_DISPLAY: 'formSettings.chartDisplay',
    SEGMENT_TIME_FREQUENCY: 'formSettings.segmentTimeFrequency',
  },
  PAGES: {
    OVERVIEW: 'pages.overview',
    CAMPAIGNS: 'pages.campaigns',
    AUDIENCE: 'pages.audience',
    TIME_SERIES: 'pages.timeSeries',
    LOCATIONS: 'pages.locations',
    DEVICES: 'pages.devices',
  },
};

export type PDFReportFormStateValues = Required<z.infer<typeof PDFReportFormSchema>>;

export const PDFReportFormSchema = z
  .object({
    type: z.nativeEnum(PDFReportType),
    env: z.string(),
    reportDetails: z.object({
      name: z.string().min(1, 'Please add a name.'),
      username: z.string(), // Hidden field
    }),
    /** Hidden fields */
    organizationDetails: z.object({
      orgName: z.string(),
      projName: z.string(),
      orgId: z.number(),
      projId: z.number(),
    }),
    formSettings: z.object({
      audienceSegment: z.union([
        /**
         * The basic select component is not able to render the default object.
         * That's the reason why we default to string.
         */
        z.string(),
        z.preprocess(
          (value) => {
            // Transform empty string into a valid default object
            if (typeof value === 'string' && value === '') {
              return { name: '', type: '' };
            }
            return value;
          },
          z.object({
            name: z.string(),
            type: z.string(),
          }),
        ),
      ]),
      locationRegion: z.string().min(1, 'Please select a region.'),
      chartDisplay: z.nativeEnum(ChartDisplay),
      segmentTimeFrequency: z.nativeEnum(SegmentTimeFrequency),
    }),
    pages: z.object({
      overview: z.boolean(),
      campaigns: z.boolean(),
      audience: z.boolean(),
      timeSeries: z.boolean(),
      locations: z.boolean(),
      devices: z.boolean(),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.pages.audience) {
      const { audienceSegment } = data.formSettings;

      // Validate that audienceSegment is not a string
      if (typeof audienceSegment === 'string') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a segment.',
          path: ['formSettings', 'audienceSegment', 'name'],
        });
      }
    }
  });
